import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { isAxiosError } from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import MessageBox from "../MessageBox/MessageBox";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Radio from "@mui/material/Radio";
import CheckIcon from "@mui/icons-material/Check";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { green, orange, red } from "@mui/material/colors";
import "./AdminPage.css";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import latinize from "latinize";
import useLogout from "../../hooks/useLogout";
import AcceptedSongsPopup from "../SongsListPopUp/AcceptedSongsPopup";
import BandChosenSongsList from "../BandChosenSongsList/BandChosenSongsList";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DownloadIcon from "@mui/icons-material/Download";
import AchievementsSegment from "../AchievementsPage/AchievementsSegment/AchievementsSegment";
import AchievementsTeamsSegment, { IAchievement } from "../AchievementsPage/AchievementsTeamsSegment/AchievementsTeamsSegment";
import { Add } from "@mui/icons-material";

interface ISubmissionDB {
  czyZagral: string;
  idZespolu: number;
  nazwa: string;
  idZgloszenia: number;
  rok: number;
  zaakceptowane: string;
  liczbaCzlonkow?: number;
  mozeEdytowac?: number;
}

interface ISubmission {
  id: number;
  bandNumber: number;
  bandName: string;
  memberCount?: number;
  played: boolean;
  accepted: boolean | null;
  locked: boolean;
}

interface GridHeaderProps {
  concertId: number;
  concertVersion: number;
  concertLocation: string;
  startDate: Date;
  currentEvent: any;
}

type FilterType = "all" | "accepted" | "rejected" | "pending";
type ScopeFilterType = "all" | "bands" | "songs";
type SystemStateKey =
  | "admin_submissionStatus"
  | "admin_songStatus"
  | "admin_exportScope"
  | "admin_filter"
  | "admin_achievementFilter";
type SystemStateMap = Partial<Record<SystemStateKey, string>>;

const SUBMISSION_URL = "admin/submissions/all/";
const SUBMISSION_FILES_URL = "admin/downloadSubmissionFiles/";
const PDF_BY_ID_URL = "admin/pdf/";
const ALL_IMAGES_URL = "admin/downloadImages/";
const STATUS_URL = "admin/saveStatus";
const BULK_PDF_URL = "admin/pdf/temp/";
const SYSTEM_STATE_URL = "admin/settings/systemState";

const START_YEAR = 2025;
const BAND_NAME_HEADER = "Nazwa Zespołu";
const SONG_NAME_HEADER = "Piosenki";
const REVIEW_HEADER = "Decyzja";
// const ACHIEVEMENTS_HEADER = "Achievements";

const NONE_SELECT_ITEM_NAME = "Wszystkie";
const ACCEPTED_SELECT_ITEM_NAME = "Zaakceptowane";
const REJECTED_SELECT_ITEM_NAME = "Odrzucone";
const NULL_SELECT_ITEM_NAME = "Oczekujące";
const DEFAULT_BAND_NAME = "=> Zespół nie posiada nazwy <=";

const formatBandDisplayName = (bandName: string | undefined, memberCount?: number) => {
  const resolvedBandName = bandName || DEFAULT_BAND_NAME;
  const resolvedMemberCount = Number.isFinite(memberCount) ? memberCount : "?";
  return `${resolvedBandName}  (${resolvedMemberCount})`;
};

const BAND_SELECTED_ITEM_NAME = "Zespoły";
const SONG_SELECTED_ITEM_NAME = "Piosenki";
const FILTER_LABELS: Record<FilterType, string> = {
  all: NONE_SELECT_ITEM_NAME,
  accepted: ACCEPTED_SELECT_ITEM_NAME,
  rejected: REJECTED_SELECT_ITEM_NAME,
  pending: NULL_SELECT_ITEM_NAME,
};
const SCOPE_LABELS: Record<ScopeFilterType, string> = {
  all: NONE_SELECT_ITEM_NAME,
  bands: BAND_SELECTED_ITEM_NAME,
  songs: SONG_SELECTED_ITEM_NAME,
};

const AdminPage = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const { registrationDisabled, setRegistrationDisabled } = useAuth();

  const [year, setYear] = useState(new Date().getFullYear());
  const [filter, setFilter] = useState<FilterType>("all");
  const [submissions, setSubmissions] = useState<ISubmission[]>([]);
  
  // Bulk PDF download filters
  const [submissionStatus, setSubmissionStatus] = useState<FilterType>("all");
  const [songStatus, setSongStatus] = useState<FilterType>("all");
  const [exportScope, setExportScope] = useState<ScopeFilterType>("all");

  const [alertMsg, setAlertMsg] = useState('');
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(false);
  const [isChangingRegistration, setIsChangingRegistration] = useState(false);
  const [concertData, setConcertData] = useState<GridHeaderProps[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [acceptedSongsBandNumber, setAcceptedSongsBandNumber] = useState<number | null>(null);
  const [expandedBandNumber, setExpandedBandNumber] = useState<number | null>(null);
  const [downloadingSubmissionId, setDownloadingSubmissionId] = useState<number | null>(null);
  const [openAchievements, setOpenAchievements] = useState(false);
  const [openTeamsAchievements, setOpenTeamsAchievements] = useState(false);
  const [achievementBandName, setAchievementBandName] = useState("");
  const [achievementBandId, setAchievementBandId] = useState<number | null>(null);
  const [bandAchievementsMap, setBandAchievementsMap] = useState<Record<number, IAchievement[]>>({});
  const [allAchievements, setAllAchievements] = useState<IAchievement[]>([]);
  const [achievementFilter, setAchievementFilter] = useState<number | "all">("all");
  const [systemDefaults, setSystemDefaults] = useState<SystemStateMap>({});
  const [isLatex, setIsLatex] = useState(false);
  const [latexSpacing, setLatexSpacing] = useState("0.8cm");

  const persistSystemDefault = async (stateName: SystemStateKey, value: string) => {
    setSystemDefaults((prev) => ({ ...prev, [stateName]: value }));
    try {
      await axiosPrivate.put(SYSTEM_STATE_URL, { stateName, value });
    } catch (error) {
      if (isAxiosError(error)) {
        console.error("Błąd zapisu domyślnego stanu:", error.response?.data);
      }
    }
  };

  const isDefault = (stateName: SystemStateKey, currentValue: string, optionValue: string) => {
    const saved = systemDefaults[stateName];
    return saved !== undefined ? saved === optionValue : currentValue === optionValue;
  };

  const getRegistration = async () => {
    try {
      const response = await axiosPrivate.get('/admin/settings/registrationStatus');
      const registrationEnabled = response.data.registrationEnabled;
      setRegistrationDisabled(!registrationEnabled);
    } catch (error) {
      console.error('Error fetching registration status:', error);
    }
  };

  // Helper function to get download button title
  const getDownloadButtonTitle = (
    submissionStatus: FilterType,
    songStatus: FilterType,
    scope: ScopeFilterType,
    year: number
  ) => {
    const submissionMapping: Record<FilterType, string> = {
      all: "wszystkimi zgłoszeniami",
      accepted: "zaakceptowanymi zgłoszeniami",
      rejected: "odrzuconymi zgłoszeniami",
      pending: "oczekującymi zgłoszeniami",
    };

    const songMapping: Record<FilterType, string> = {
      all: "wszystkimi piosenkami",
      accepted: "zaakceptowanymi piosenkami",
      rejected: "odrzuconymi piosenkami",
      pending: "oczekującymi piosenkami",
    };

    const scopeMapping: Record<ScopeFilterType, string> = {
      all: "Pobierz PDF z",
      bands: "Pobierz PDF zespołów z",
      songs: "Pobierz PDF piosenek z",
    };

    if (scope === "all") {
      return `${scopeMapping[scope]} ${submissionMapping[submissionStatus]} i ${songMapping[songStatus]} - ${year}`;
    } else if (scope === "bands") {
      return `${scopeMapping[scope]} ${submissionMapping[submissionStatus]} - ${year}`;
    } else {
      return `${scopeMapping[scope]} ${songMapping[songStatus]} - ${year}`;
    }
  };

  // helper function to map frontend filter values to backend expected values
  const mapToBackendValue = (value: FilterType | ScopeFilterType): string => {
    const mapping: Record<string, string> = {
      all: "All",
      accepted: "Accepted",
      rejected: "Rejected",
      pending: "Pending",
      bands: "Bands",
      songs: "Songs",
    };
    return mapping[value] || value;
  };

  const loadSystemDefaults = async () => {
    try {
      const response = await axiosPrivate.get(SYSTEM_STATE_URL);
      const loaded = (response.data?.states ?? {}) as SystemStateMap;
      setSystemDefaults(loaded);

      const FILTER_VALS: FilterType[] = ["all", "accepted", "rejected", "pending"];
      const SONG_VALS: FilterType[] = ["all", "accepted", "rejected"];
      const SCOPE_VALS: ScopeFilterType[] = ["all", "bands", "songs"];

      if (loaded.admin_submissionStatus && FILTER_VALS.includes(loaded.admin_submissionStatus as FilterType))
        setSubmissionStatus(loaded.admin_submissionStatus as FilterType);
      if (loaded.admin_songStatus && SONG_VALS.includes(loaded.admin_songStatus as FilterType))
        setSongStatus(loaded.admin_songStatus as FilterType);
      if (loaded.admin_exportScope && SCOPE_VALS.includes(loaded.admin_exportScope as ScopeFilterType))
        setExportScope(loaded.admin_exportScope as ScopeFilterType);
      if (loaded.admin_filter && FILTER_VALS.includes(loaded.admin_filter as FilterType))
        setFilter(loaded.admin_filter as FilterType);
      if (loaded.admin_achievementFilter) {
        if (loaded.admin_achievementFilter === "all") setAchievementFilter("all");
        else if (/^\d+$/.test(loaded.admin_achievementFilter)) setAchievementFilter(Number(loaded.admin_achievementFilter));
      }
    } catch (error) {
      console.error("Błąd pobierania domyślnych stanów:", error);
    }
  };

  // Function to toggle registration status
  const toggleRegistration = async () => {
    setIsChangingRegistration(true);
    try {
      const response = await axiosPrivate.post(
        '/admin/settings/registrationStatus',
        { registrationEnabled: registrationDisabled } // registrationEnabled = opposite of registrationDisabled
      );

      setRegistrationDisabled(!registrationDisabled);

//       console.log('Registration status updated:', response.data);
    setOpen(true);
    {registrationDisabled ? setAlertMsg('Rejestracja jest dostępna dla nowych użytkowników') : setAlertMsg('Nowi użytkownicy nie mogą się rejestrować')}
    setResult(true);

    } catch (error) {
      console.error('Error changing registration status:', error);

      if (isAxiosError(error)) {
        if (error.response?.status === 403) {
          alert('Nie masz uprawnień administratora');
          navigate("/", { state: { from: location }, replace: true });
        } else {
          alert(`Błąd: ${error.response?.data?.message || error.message}`);
        }
      } else {
        alert('Nie udało się zmienić statusu rejestracji');
      }
    } finally {
      setIsChangingRegistration(false);
    }
  };

  // Bulk PDF download function
  const downloadBulkPDF = async () => {
    if (isDownloading) return;
    
    // Check if there are any submissions for the selected year
    const filteredSubmissions = submissions.filter(
      (s) =>
        submissionStatus === "all" ||
        (submissionStatus === "accepted" && s.accepted === true) ||
        (submissionStatus === "rejected" && s.accepted === false) ||
        (submissionStatus === "pending" && s.accepted === null)
    );
    
    // everything is translated thru google translate - may not be write 

    console.log("Total submissions:", submissions.length);
    console.log("All submissions:", submissions);
    console.log("Filtered submissions for status '" + submissionStatus + "':", filteredSubmissions.length);
    
    if (filteredSubmissions.length === 0 && submissionStatus !== "all") {
      alert(`Nie znaleziono zgłoszeń ze statusem "${submissionStatus}" dla roku ${year}.`);
      return;
    }

    if (submissions.length === 0) {
      alert(`Nie znaleziono żadnych zgłoszeń dla roku ${year}.`);
      return;
    }
    
    setIsDownloading(true);
    const controller = new AbortController();

    try {
      // Map frontend values to backend expected values (capitalized)
      const applicationStatus = mapToBackendValue(submissionStatus);
      const songStatusMapped = mapToBackendValue(songStatus);
      const dataScope = mapToBackendValue(exportScope);
      
      // Construct the URL for bulk PDF download (GET request with path parameters)
      const url = `${BULK_PDF_URL}${year}/${applicationStatus}/${songStatusMapped}/${dataScope}`;
      
      console.log("Downloading bulk PDF with URL:", url);
      console.log("Parameters:", { year, applicationStatus, songStatusMapped, dataScope });
      console.log("Available submissions count:", submissions.length);
      console.log("Filtered submissions count:", filteredSubmissions.length);
      
      const response = await axiosPrivate.get(
        url,
        {
          signal: controller.signal,
          responseType: "blob",
        }
      );

      // Check if the response is a PDF file
      const contentType = response.headers["content-type"];
      if (!contentType || !contentType.includes("pdf")) {
        console.error("Invalid file type received:", contentType);
        alert("Błąd: Nie udało się pobrać pliku PDF.");
        return;
      }

      // Create download link
      const fileUrl = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = fileUrl;
      
      // Generate filename based on filters
      const filename = `zgloszenia_${submissionStatus}_${songStatus}_${exportScope}_${year}.pdf`;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        URL.revokeObjectURL(fileUrl);
        document.body.removeChild(a);
      }, 100);
      
      console.log("PDF downloaded successfully:", filename);
      
    } catch (error) {
      console.error("Error downloading bulk PDF:", error);
      
      if (isAxiosError(error)) {
        console.error("Error details:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
        });
        
        // Try to read error message from blob response if available
        if (error.response?.data && error.response.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            console.error("Error response body:", errorText);
            // Try to parse as JSON, otherwise use as text
            try {
              const errorJson = JSON.parse(errorText);
              console.error("Parsed error JSON:", errorJson);
            } catch {
              console.error("Error is plain text:", errorText);
            }
          } catch (e) {
            console.error("Could not read error blob:", e);
          }
        }
        
        if (error.response?.status === 403) {
          navigate("/", { state: { from: location }, replace: true });
        } else if (error.response?.status === 404) { //not found error
          alert("Nie znaleziono danych spełniających wybrane kryteria. Sprawdź czy istnieją zgłoszenia dla wybranego roku i statusu.");
        } else if (error.response?.status === 500) { 
          alert("Błąd serwera podczas generowania PDF. Sprawdź konsolę przeglądarki dla szczegółów.");
        } else {
          alert(`Nie udało się pobrać pliku PDF. Status: ${error.response?.status || 'unknown'}`);
        }
      } else {
        alert("Wystąpił nieoczekiwany błąd podczas pobierania pliku.");
      }
    } finally {
      setIsDownloading(false);
      // Only abort if there was an error (controller will be cleaned up on component unmount)
    }
  };

  const downloadIndividualPDF = async (submissionId: number, bandName: string) => {
    if (downloadingSubmissionId !== null) return;
    setDownloadingSubmissionId(submissionId);
    const controller = new AbortController();
    try {
      const response = await axiosPrivate.get(`${PDF_BY_ID_URL}${submissionId}`, {
        signal: controller.signal,
        responseType: "blob",
      });
      const contentType = response.headers["content-type"];
      if (!contentType || !contentType.includes("pdf")) {
        alert("Błąd: Nie udało się pobrać pliku PDF.");
        return;
      }
      const fileUrl = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = fileUrl;
      const safeName = (bandName || "zespol").replace(/\s+/g, "_");
      a.download = `${safeName}_zgloszenie.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(fileUrl);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 403) {
          navigate("/", { state: { from: location }, replace: true });
        } else if (error.response?.status === 404) {
          alert("Nie znaleziono PDF dla tego zgłoszenia.");
        } else {
          alert("Nie udało się pobrać PDF.");
        }
      } else {
        alert("Wystąpił błąd podczas pobierania PDF.");
      }
    } finally {
      setDownloadingSubmissionId(null);
    }
  };

  const downloadChronologicalBandsPDF = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    const controller = new AbortController();
    try {
      const url = `${BULK_PDF_URL}${year}/All/All/Bands`;
      const response = await axiosPrivate.get(url, {
        signal: controller.signal,
        responseType: "blob",
      });
      const contentType = response.headers["content-type"];
      if (!contentType || !contentType.includes("pdf")) {
        alert("Błąd: Nie udało się pobrać pliku PDF.");
        return;
      }
      const fileUrl = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = fileUrl;
      a.download = `chronologiczna_lista_zespolow_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(fileUrl);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 403) {
          navigate("/", { state: { from: location }, replace: true });
        } else if (error.response?.status === 404) {
          alert("Nie znaleziono danych dla wybranego roku.");
        } else {
          alert("Nie udało się pobrać PDF.");
        }
      } else {
        alert("Wystąpił błąd podczas pobierania PDF.");
      }
    } finally {
      setIsDownloading(false);
    }
  };


  const navigateToClosestConcert = async () => {
    try {
      const response = await axiosPrivate.get('concerts/all');
      const concerts = response.data;

      if (response.data && response.status === 200) {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        today.setHours(0, 0, 0, 0);

        const futureConcerts = concerts.filter((concert: any) => {
          const [day, month, year] = concert.dataKoncertu.split("-").map(Number);
          const concertDate = new Date(year, month - 1, day);
          concertDate.setHours(0, 0, 0, 0);
          return concertDate >= today;
        });

        if (futureConcerts.length === 0) {
          navigate('/concertList');
          return;
        }

        const closestConcert = futureConcerts.reduce((prev: any, curr: any) => {
          const [prevDay, prevMonth, prevYear] = prev.dataKoncertu.split("-").map(Number);
          const prevDate = new Date(prevYear, prevMonth - 1, prevDay);
          const [currDay, currMonth, currYear] = curr.dataKoncertu.split("-").map(Number);
          const currDate = new Date(currYear, currMonth - 1, currDay);
          return currDate < prevDate ? curr : prev;
        }, futureConcerts[0]);

        const [day, month, year] = closestConcert.dataKoncertu.split("-").map(Number);
        setConcertData([{
          concertId: closestConcert.idKoncertu,
          concertVersion: closestConcert.wersja,
          concertLocation: closestConcert.miejsceKoncertu,
          startDate: new Date(year, month - 1, day),
          currentEvent: null,
        }]);

        navigate(`/concert/${closestConcert.idKoncertu}`);
      } else {
        throw new Error("Network response failed");
      }
    } catch (error) {
      console.error("Error fetching concert data:", error);
    }
  };

  const toggleLock = async (teamId: number, locked: boolean) => {
    try {
      const currentSubmission = submissions.find(
        (sub) => sub.bandNumber === teamId
      );
      if (!currentSubmission) return;

      let newEditState = locked ? 1 : 0;

      const response = await axiosPrivate.patch(
        `admin/teams/${teamId}/lock/${newEditState}`,
        {},
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200) {
        setSubmissions((prev) =>
          prev.map((sub) =>
            sub.bandNumber === teamId
              ? { ...sub, locked: newEditState === 0 }
              : sub
          )
        );
      } else {
        console.error("Nie udało się zaktualizować stanu blokady w bazie.");
      }
    } catch (error) {
      console.error("Błąd podczas aktualizacji stanu blokady:", error);
    }
  };

  // changed to handle null/empty zaakceptowane as pending and added more logging
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const getSubmissions = async () => {
      try {
        const response = await axiosPrivate.get(SUBMISSION_URL + year, {
          signal: controller.signal,
        });

        console.log("Raw API response:", response.data);

        if (isMounted) {
          const translatedSubmissions = response.data.map(
            (s: ISubmissionDB) => {
              // Handle null/empty zaakceptowane as pending
              let acceptedValue: boolean | null = null;
              if (s.zaakceptowane === "tak") {
                acceptedValue = true;
              } else if (s.zaakceptowane === "nie") {
                acceptedValue = false;
              } else {
                acceptedValue = null; // pending - could be NULL, undefined, empty string, etc.
              }
              
              return {
                id: s.idZgloszenia,
                bandNumber: s.idZespolu,
                bandName: s.nazwa,
                memberCount: s.liczbaCzlonkow,
                played: s.czyZagral === "tak",
                accepted: acceptedValue,
                locked: s.mozeEdytowac === 0,
              };
            }
          );
          console.log("Translated submissions:", translatedSubmissions);
          setSubmissions(translatedSubmissions);
        }
      } catch (error) {
        console.error("Error fetching submissions:", error);
        if (isAxiosError(error) && error.response?.status === 403) {
          navigate("/", { state: { from: location }, replace: true });
        }
      }
    };
    // console.log(response.data);

    getSubmissions();
    getRegistration(); // Fetch registration status on mount

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [year]);

  useEffect(() => {
    getRegistration();
  }, []);

  useEffect(() => {
    loadSystemDefaults();
  }, []);

  useEffect(() => {
    axiosPrivate.get('/achievements')
      .then((res) => {
        setAllAchievements(res.data as IAchievement[]);
      })
      .catch((error) => {
        console.error('Error fetching all achievements:', error);
      });
  }, [axiosPrivate]);

  useEffect(() => {
    if (submissions.length === 0) return;
    submissions.forEach((sub) => {
      axiosPrivate.get(`/achievements/bands/${sub.bandNumber}`)
        .then((res) => {
          const ids = res.data.map((item: any) => item.idOsiagniecia);
          return axiosPrivate.get('/achievements').then((res2) => {
            const linked = res2.data.filter((a: IAchievement) => ids.includes(a.idOsiagniecia));
            setBandAchievementsMap((prev) => ({ ...prev, [sub.bandNumber]: linked }));
          });
        })
        .catch(() => {
          setBandAchievementsMap((prev) => ({ ...prev, [sub.bandNumber]: [] }));
        });
    });
  }, [submissions, axiosPrivate]);

  const handlerChangeAchievements = (bandNumber: number, bandName: string) => {
    setAchievementBandId(bandNumber);
    setAchievementBandName(bandName);
    setOpenTeamsAchievements(true);
  };

  const handleBandAchievementsClose = async () => {
    setOpenTeamsAchievements(false);
    if (achievementBandId) {
      try {
        const res = await axiosPrivate.get(`/achievements/bands/${achievementBandId}`);
        const ids = res.data.map((item: any) => item.idOsiagniecia);
        const res2 = await axiosPrivate.get('/achievements');
        const linked = res2.data.filter((a: IAchievement) => ids.includes(a.idOsiagniecia));
        setBandAchievementsMap((prev) => ({ ...prev, [achievementBandId]: linked }));
      } catch (error) {
        console.error('Error loading achievements:', error);
      }
    }
  };

  const selectedAchievementName =
    achievementFilter === "all"
      ? NONE_SELECT_ITEM_NAME
      : allAchievements.find((a) => a.idOsiagniecia === achievementFilter)?.nazwa ?? String(achievementFilter);

  // same
  const GenerateList = () => (
    <>

      {submissions
        .filter(
          (s) =>
            filter === "all" ||
            (filter === "accepted" && s.accepted === true) ||
            (filter === "rejected" && s.accepted === false) ||
            (filter === "pending" && s.accepted === null)
        )
        .filter((s) =>
          achievementFilter === "all" ||
          (bandAchievementsMap[s.bandNumber] ?? []).some(
            (a) => a.idOsiagniecia === achievementFilter
          )
        )
        .map((submission) => (
          <ListItem key={submission.id} disablePadding className="admin-list-item-wrap">
            <div className="list-row">
              <div className="cell-stack band-name-cell">
                <div className="band-name-row">
                  <p
                    className="band-name-clickable"
                    onClick={() => downloadIndividualPDF(submission.id, submission.bandName || "")}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        downloadIndividualPDF(submission.id, submission.bandName || "");
                      }
                    }}
                  >
                    {formatBandDisplayName(submission.bandName, submission.memberCount)}
                  </p>
                  <span
                    className="band-expand-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedBandNumber((prev) => (prev === submission.bandNumber ? null : submission.bandNumber));
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpandedBandNumber((prev) => (prev === submission.bandNumber ? null : submission.bandNumber));
                      }
                    }}
                  >
                    {expandedBandNumber === submission.bandNumber ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </span>
                </div>
                {expandedBandNumber === submission.bandNumber && (
                  <div className="band-chosen-songs">
                    <BandChosenSongsList bandNumber={submission.bandNumber} year={year} />
                  </div>
                )}
              </div>
              <div className="cell-stack">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setAcceptedSongsBandNumber(submission.bandNumber)}
                  sx={{
                    borderColor: "#ff9800",
                    color: "#ff9800",
                    "&:hover": { borderColor: "#fb8c00", backgroundColor: "rgba(255,152,0,0.08)" },
                  }}
                >
                  Wybierz utwory
                </Button>
              </div>
              <div className="row">
                {/* pending checkbox */}
                <Checkbox
                  checked={submission.accepted === null}
                  onChange={() => {
                    saveStatus(submission, null);
                  }}
                  icon={<HourglassEmptyIcon sx={{ color: "#ffffff" }} />}
                  checkedIcon={<HourglassEmptyIcon sx={{ color: "#ff9800" }} />}
                />
                {/* accpted */}
                <Checkbox
                  checked={submission.accepted === true}
                  onChange={() => saveStatus(submission, true)}
                  icon={<CheckIcon sx={{ color: "#ffffff" }} />}
                  checkedIcon={<CheckIcon sx={{ color: green[500] }} />}
                />
                {/* rejected */}
                <Checkbox
                  checked={submission.accepted === false}
                  onChange={() => saveStatus(submission, false)}
                  icon={<BlockIcon sx={{ color: "#ffffff" }} />}
                  checkedIcon={<BlockIcon sx={{ color: red[500] }} />}
                />

                <Checkbox
                  checked={false}
                  onClick={() => {
                    navigate("/band");
                    sessionStorage.setItem(
                      "editedBandId",
                      String(submission.bandNumber)
                    );
                  }}
                  icon={<EditIcon sx={{ color: "#ffffff" }} />}
                />
                <Checkbox
                  checked={submission.locked}
                  onChange={() =>
                    toggleLock(submission.bandNumber, submission.locked)
                  }
                  icon={<LockOpenIcon sx={{ color: green[500] }} />}
                  checkedIcon={<LockIcon sx={{ color: red[500] }} />}
                />
              </div>
              <div className="cell-stack">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handlerChangeAchievements(submission.bandNumber, submission.bandName || DEFAULT_BAND_NAME)}
                  sx={{ minWidth: 50, alignSelf: 'flex-end',mr:10 }}
                >
                  <EmojiEventsIcon />
                </Button>
                <div className="achievements-preview">
                  {(bandAchievementsMap[submission.bandNumber] ?? [])
                    .map((a) => a.nazwa)
                    .join(", ") || "—"}
                </div>
              </div>
            </div>
          </ListItem>

        ))}
    </>
  );


const saveStatus = async (
  submission: ISubmission,
  status: boolean | null
) => {

  try {
    console.log("Saving status:", { submissionId: submission.id, status });
    
    const payload = {
      submissionId: submission.id,
      accepted: status === null ? null : (status ? "tak" : "nie"),
    };
    
    console.log("Payload being sent:", payload);
    
    const response = await axiosPrivate.post(
      STATUS_URL,
      payload,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    
    console.log("Status save response:", response);
    console.log("Status save response data:", response.data);

    setSubmissions((prev) =>
      prev.map((s) =>
        s.id === submission.id ? { ...s, accepted: status } : s
      )
    );

    setResult(true);
    setAlertMsg('');
  } catch (error:any){
    console.error("ERROR RESPONSE:", error?.response);
    console.error("ERROR DATA:", error?.response?.data);
    console.error("Full error:", error);
    setResult(false);
    setAlertMsg('')
  }

  setOpen(true);
};

  const updateBandOrder = async () => {
    const controller = new AbortController();

    try {
      const response = await axiosPrivate.post('bands/band-order', {}, {
        signal: controller.signal,
      });

      if (response.status === 200) {
        alert('Kolejność zespołów została zaktualizowana pomyślnie');
      }
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 403) {
          navigate("/", { state: { from: location }, replace: true });
        } else {
          console.error("Błąd podczas aktualizacji kolejności zespołów:", error);
          alert("Nie udało się zaktualizować kolejności zespołów");
        }
      }
    } finally {
      controller.abort();
    }
  };

  const handleDownloadBandsByAchievement = async () => {
    if (achievementFilter === "all") {
      alert("Proszę wybrać osiągnięcie w filtrze, aby pobrać listę zespołów.");
      return;
    }

    try {
      const response = await axiosPrivate.get("/achievements/bands-by-year", {
        params: { year, achievementId: achievementFilter },
      });
      const bands: string[] = response.data;
      let fileName: string;
      let content: string;
      if (isLatex) {
        fileName = `${year}_${selectedAchievementName}.tex`;
        const items = bands.length > 0
          ? bands.map((bandName) => `  ${bandName?.trim() || DEFAULT_BAND_NAME} \\\\[${latexSpacing}]`).join("\n")
          : `   Brak zespołów \\[${latexSpacing}]`;
        content = items;
      } else {
        fileName = `${year}_${selectedAchievementName}.txt`;
        content = bands.length > 0
          ? bands.map((bandName) => bandName?.trim() || DEFAULT_BAND_NAME).join("\n")
          : "Brak zespołów";
      }
      const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      console.error("Error downloading bands by achievement:", error);
      alert("Błąd podczas pobierania listy zespołów.");
    }
  };

  return (
      <div  className="admin-container">
      <div className="admin-header">
        <div>
          <button className="logout" onClick={logout}>
            Wyloguj się
          </button>
        </div>
      </div>
      
      <div className="admin-content">
        {/* Bulk PDF Download Section */}
        <div className="admin-top-bar">
          <h4>Pobierz PDF - Wybierz swoje kryteria!</h4>
          
          <div className="bulk-filters">
            {/* Submission Status Filter */}
            <div className="filter-select1">
              <label>Status przesłania:</label>
              <Select
                value={submissionStatus}
                renderValue={(selected) => FILTER_LABELS[selected as FilterType]}
                onChange={(e) => {
                  const v = e.target.value as FilterType;
                  setSubmissionStatus(v);
                  persistSystemDefault("admin_submissionStatus", v);
                }}
              >
                <MenuItem value="all"><Radio size="small" checked={isDefault("admin_submissionStatus", submissionStatus, "all")} />{NONE_SELECT_ITEM_NAME}</MenuItem>
                <MenuItem value="accepted"><Radio size="small" checked={isDefault("admin_submissionStatus", submissionStatus, "accepted")} />{ACCEPTED_SELECT_ITEM_NAME}</MenuItem>
                <MenuItem value="rejected"><Radio size="small" checked={isDefault("admin_submissionStatus", submissionStatus, "rejected")} />{REJECTED_SELECT_ITEM_NAME}</MenuItem>
                <MenuItem value="pending"><Radio size="small" checked={isDefault("admin_submissionStatus", submissionStatus, "pending")} />{NULL_SELECT_ITEM_NAME}</MenuItem>
              </Select>
            </div>

            {/* Song Status Filter */}
            <div className="filter-select1">
              <label>Status Piosenki:</label>
              <Select
                value={songStatus}
                renderValue={(selected) => FILTER_LABELS[selected as FilterType]}
                onChange={(e) => {
                  const v = e.target.value as FilterType;
                  setSongStatus(v);
                  persistSystemDefault("admin_songStatus", v);
                }}
              >
                <MenuItem value="all"><Radio size="small" checked={isDefault("admin_songStatus", songStatus, "all")} />{NONE_SELECT_ITEM_NAME}</MenuItem>
                <MenuItem value="accepted"><Radio size="small" checked={isDefault("admin_songStatus", songStatus, "accepted")} />{ACCEPTED_SELECT_ITEM_NAME}</MenuItem>
                <MenuItem value="rejected"><Radio size="small" checked={isDefault("admin_songStatus", songStatus, "rejected")} />{REJECTED_SELECT_ITEM_NAME}</MenuItem>
                {/* <MenuItem value="pending">{NULL_SELECT_ITEM_NAME}</MenuItem>  */}
              </Select>
            </div>
            
            {/* Export Scope Filter */}
            <div className="filter-select1">
              <label>Zakres Danych:</label>
              <Select
                value={exportScope}
                renderValue={(selected) => SCOPE_LABELS[selected as ScopeFilterType]}
                onChange={(e) => {
                  const v = e.target.value as ScopeFilterType;
                  setExportScope(v);
                  persistSystemDefault("admin_exportScope", v);
                }}
              >
                <MenuItem value="all"><Radio size="small" checked={isDefault("admin_exportScope", exportScope, "all")} />{NONE_SELECT_ITEM_NAME}</MenuItem>
                <MenuItem value="bands"><Radio size="small" checked={isDefault("admin_exportScope", exportScope, "bands")} />{BAND_SELECTED_ITEM_NAME}</MenuItem>
                <MenuItem value="songs"><Radio size="small" checked={isDefault("admin_exportScope", exportScope, "songs")} />{SONG_SELECTED_ITEM_NAME}</MenuItem>
              </Select>
            </div>

            {/* Download Button */}
            <div className="export-btn">
              <Button 
                variant="contained"
                onClick={downloadBulkPDF}
                disabled={isDownloading}
                size="large"
                sx={{
                  minWidth: 280,
                  backgroundColor: '#363636', color:"white",
                  '&:hover': { backgroundColor: '#222222', color:"white" }, borderRadius:"10px",
                }}
              >
                {isDownloading ? 'Pobieranie...' : getDownloadButtonTitle(submissionStatus, songStatus, exportScope, year)}
              </Button>
            </div>
          </div>
        </div>


        {/* Filter Section */}
        <div className="filter-section">
          <div className="selection-btns">
            {/* Display Filter (for UI only)  - show submission in english*/}
            <div className="filter-select1">
              <label>Pokaż Poddanie:</label>
              <Select
                value={filter}
                renderValue={(selected) => FILTER_LABELS[selected as FilterType]}
                onChange={(event: SelectChangeEvent) => {
                  const v = event.target.value as FilterType;
                  setFilter(v);
                  persistSystemDefault("admin_filter", v);
                }}
              >
                <MenuItem value="all"><Radio size="small" checked={isDefault("admin_filter", filter, "all")} />{NONE_SELECT_ITEM_NAME}</MenuItem>
                <MenuItem value="accepted"><Radio size="small" checked={isDefault("admin_filter", filter, "accepted")} />{ACCEPTED_SELECT_ITEM_NAME}</MenuItem>
                <MenuItem value="rejected"><Radio size="small" checked={isDefault("admin_filter", filter, "rejected")} />{REJECTED_SELECT_ITEM_NAME}</MenuItem>
                <MenuItem value="pending"><Radio size="small" checked={isDefault("admin_filter", filter, "pending")} />{NULL_SELECT_ITEM_NAME}</MenuItem>
              </Select>
            </div>

            {/* Year Filter */}
            <div className="filter-select1">
              <label>Rok:</label>
              <Select
                value={year.toString()}
                onChange={(event: SelectChangeEvent) =>
                  setYear(Number(event.target.value))
                }
              >
                {[...Array(new Date().getFullYear() - START_YEAR + 1).keys()].map((n) => (
                  <MenuItem key={n} value={n + START_YEAR}>
                    {n + START_YEAR}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className="filter-select1">
              <label>Osiągnięcia:</label>
              <Select
                value={achievementFilter.toString()}
                renderValue={(selected) => {
                  const selectedValue = selected as string;
                  if (selectedValue === "all") return NONE_SELECT_ITEM_NAME;
                  const selectedAchievement = allAchievements.find(
                    (a) => a.idOsiagniecia.toString() === selectedValue
                  );
                  return selectedAchievement?.nazwa ?? NONE_SELECT_ITEM_NAME;
                }}
                onChange={(event: SelectChangeEvent) => {
                  const val = event.target.value;
                  setAchievementFilter(val === "all" ? "all" : Number(val));
                  persistSystemDefault("admin_achievementFilter", val);
                }}
              >
                <MenuItem value="all"><Radio size="small" checked={isDefault("admin_achievementFilter", achievementFilter.toString(), "all")} />{NONE_SELECT_ITEM_NAME}</MenuItem>
                {allAchievements.map((a) => (
                  <MenuItem key={a.idOsiagniecia} value={a.idOsiagniecia}>
                    <Radio size="small" checked={isDefault("admin_achievementFilter", achievementFilter.toString(), a.idOsiagniecia.toString())} />
                    {a.nazwa}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div >
          <div className="filter-actions">
            <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
              <Checkbox
                checked={isLatex}
                onChange={(e) => setIsLatex(e.target.checked)}
                size="small"
                color="primary"
                sx={{ color: "white" }}
              />
              LaTeX
            </label>
            {isLatex && (
              <input
                type="text"
                value={latexSpacing}
                onChange={(e) => setLatexSpacing(e.target.value)}
                placeholder="0.8cm"
                style={{
                  width: 70,
                  background: "transparent",
                  border: "1px solid white",
                  borderRadius: 4,
                  color: "white",
                  padding: "2px 6px",
                  fontSize: 14,
                }}
              />
            )}
            <Button
              color={"primary"}
              variant="outlined"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadBandsByAchievement}
              title={
                achievementFilter === "all"
                  ? "Wybierz osiągnięcie, aby pobrać listę zespołów"
                  : `Pobierz listę zespołów z osiągnięciem dla roku ${year}`
              }
              sx={{
                minWidth: 100,
                height: 48,
                borderRadius: "10px",
              }}
            >
              {achievementFilter === "all" ? "Zespoły z wybranym osiągnięciem" : `"${year}_${selectedAchievementName}"`}
            </Button>
            <Button
              color="info"
              variant="outlined"
              size="large"
              onClick={() => setOpenAchievements(true)}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 100,
                height: 48,
                backgroundColor: '#363636', color: "white",
                '&:hover': { backgroundColor: '#222222', color: "white" }, borderRadius: "10px",
              }}
            >
              Osiągnięcia
            </Button>
            <div className="concert-management" onClick={() => navigateToClosestConcert()}>
              Zarządzanie koncertami
            </div>
          </div>
        </div>


        {/* List Container */}
        <div className="list-container">
          <div className="list-header">
            <p>{BAND_NAME_HEADER}</p>
            <p>{SONG_NAME_HEADER}</p>
            <p>{REVIEW_HEADER}</p>
            <p>Osiągnięcia</p>
          </div>
          <List disablePadding>
            <GenerateList />
          </List>
        </div>

        {/* Admin Options */}
        <div className="admin-options">
          {/* Registration Management Section */}
            <div className="admin_registration_change">
              <Button
                variant={registrationDisabled ? "contained" : "outlined"}
                color={registrationDisabled ? "error" : "success"}
                onClick={toggleRegistration}
                disabled={isChangingRegistration}
                size="medium"
                sx={{
                  minWidth: 200,
                  backgroundColor: registrationDisabled ? '#d32f2f' : '#4caf50',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: registrationDisabled ? '#b71c1c' : '#388e3c',
                  },
                }}
              >
                {isChangingRegistration ? '⏳ Zmiana...' : (registrationDisabled ? '🔒 Rejestracja WYŁĄCZONA' : '🔓 Rejestracja WŁĄCZONA')}
              </Button>
            </div>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={updateBandOrder}
          >
            Zaktualizuj kolejność zespołów w PDF
          </Button>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={downloadChronologicalBandsPDF}
            disabled={isDownloading}
          >
            {isDownloading ? "Pobieranie..." : "Chronologiczna lista wszystkich zespołów w formacie PDF"}
          </Button>
        </div>
      </div>
      
      {/* opening for each band - only shows accepted songs to play */}
      <MessageBox open={open} setOpen={setOpen} message={alertMsg} error={!result} />
      {acceptedSongsBandNumber !== null && (
        <AcceptedSongsPopup
          bandNumber={acceptedSongsBandNumber}
          bandName={submissions.find((s) => s.bandNumber === acceptedSongsBandNumber)?.bandName ?? ''}
          year={year}
          open={true}
          onClose={() => setAcceptedSongsBandNumber(null)}
        />

      )}

      {/* Dialog for Achievements */}
      <Dialog
        open={openAchievements}
        onClose={() => setOpenAchievements(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: 'none',
            minHeight: 'none',
            overflow: 'hidden'
          },
        }}
      >
        <DialogTitle>
          Osiągnięcia
          <IconButton
            aria-label="close"
            onClick={() => setOpenAchievements(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent >
          <AchievementsSegment />
        </DialogContent>
      </Dialog>
      {/* Dialog for add Achievements for Band */}
      <Dialog

        open={openTeamsAchievements}
        onClose={handleBandAchievementsClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: 'none',
            minHeight:'none',
            overflow:'hidden'
          },
        }}
      >
        <DialogTitle>
          Osiągnięcia Zespołu "{achievementBandName}"
          <IconButton
            aria-label="close"
            onClick={handleBandAchievementsClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <AchievementsTeamsSegment bandId={achievementBandId} />
        </DialogContent>
      </Dialog>
    </div>

  );
};

export default AdminPage;
